/**
 * The camera rig, built on `camera-controls`.
 *
 * It replaces a hand-rolled orbit that had two problems: wheel `deltaY` was used
 * raw, so zoom speed differed ~35× between browsers reporting pixels (Chrome)
 * and lines (Firefox), and dolly always moved toward the orbit target rather
 * than the pointer. This rig normalises the wheel, dollies to the cursor and
 * damps every move.
 *
 * Buttons keep the original scheme — left rotates, right or ⇧-left pans — so the
 * "right-click without moving deletes the part under the pointer" gesture still
 * works. `camera-controls` never calls `stopPropagation`, so the Engine's own
 * pointer handlers see every event alongside it. It has no modifier mapping of
 * its own, so ⇧-drag is implemented here by swapping the left button's action
 * while Shift is held.
 */

import CameraControls from "camera-controls";
import {
  Box3,
  Matrix4,
  type PerspectiveCamera,
  Quaternion,
  Raycaster,
  Sphere,
  Spherical,
  Vector2,
  Vector3,
  Vector4,
} from "three";

CameraControls.install({
  THREE: { Box3, Matrix4, Quaternion, Raycaster, Sphere, Spherical, Vector2, Vector3, Vector4 },
});

/** Closest the camera may get to its target, metres. */
const MIN_DISTANCE = 0.6;

/** Furthest the camera may get from its target, metres. */
const MAX_DISTANCE = 180;

/**
 * Polar limits. Near 0 is a true top-down plan view; the upper bound stops just
 * short of the horizon so the camera never ends up under the ground plane.
 */
const MIN_POLAR_ANGLE = 0.01;
const MAX_POLAR_ANGLE = Math.PI / 2 - 0.02;

/** Damping, seconds. Lower is snappier. */
const SMOOTH_TIME = 0.12;
const DRAGGING_SMOOTH_TIME = 0.04;

/** Padding around a fitted box, as a fraction of its size. */
const FIT_PADDING = 0.25;

/** The left button's action, swapped while Shift is held. */
type LeftAction = typeof CameraControls.ACTION.ROTATE | typeof CameraControls.ACTION.TRUCK;

export type CameraRig = Readonly<{
  controls: CameraControls;
  /** Advance the damping. Returns true when something moved. */
  update: (delta: number) => boolean;
  /** Frame a bounding box, animating unless told otherwise. */
  fit: (box: Box3, animate?: boolean) => void;
  /** Re-pivot around a point without moving the camera. */
  setPivot: (point: Vector3) => void;
  /** Suspend camera gestures — the Move tool takes the left button while dragging. */
  setEnabled: (enabled: boolean) => void;
  /** The default view, used when there is nothing to frame. */
  reset: (animate?: boolean) => void;
  dispose: () => void;
}>;

export function createCameraRig(camera: PerspectiveCamera, domElement: HTMLElement): CameraRig {
  const controls = new CameraControls(camera, domElement);

  controls.minDistance = MIN_DISTANCE;
  controls.maxDistance = MAX_DISTANCE;
  controls.minPolarAngle = MIN_POLAR_ANGLE;
  controls.maxPolarAngle = MAX_POLAR_ANGLE;
  controls.smoothTime = SMOOTH_TIME;
  controls.draggingSmoothTime = DRAGGING_SMOOTH_TIME;
  controls.dollyToCursor = true;
  controls.infinityDolly = false;

  const { ACTION } = CameraControls;
  controls.mouseButtons.left = ACTION.ROTATE;
  controls.mouseButtons.right = ACTION.TRUCK;
  controls.mouseButtons.middle = ACTION.TRUCK;
  controls.mouseButtons.wheel = ACTION.DOLLY;
  controls.touches.one = ACTION.TOUCH_ROTATE;
  controls.touches.two = ACTION.TOUCH_DOLLY_TRUCK;
  controls.touches.three = ACTION.TOUCH_TRUCK;

  // camera-controls has no modifier mapping, so ⇧ swaps the left button to pan.
  const setLeft = (action: LeftAction): void => {
    controls.mouseButtons.left = action;
  };
  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === "Shift") {
      setLeft(ACTION.TRUCK);
    }
  };
  const onKeyUp = (event: KeyboardEvent): void => {
    if (event.key === "Shift") {
      setLeft(ACTION.ROTATE);
    }
  };
  // Shift released while the window was unfocused would otherwise stick.
  const onBlur = (): void => setLeft(ACTION.ROTATE);

  window.addEventListener("keydown", onKeyDown);
  window.addEventListener("keyup", onKeyUp);
  window.addEventListener("blur", onBlur);

  function reset(animate = false): void {
    void controls.setLookAt(11, 8, 11, 6, 1.5, 1, animate);
  }

  function fit(box: Box3, animate = true): void {
    if (box.isEmpty()) {
      reset(animate);
      return;
    }

    const size = box.getSize(new Vector3());
    const padding = Math.max(size.x, size.y, size.z) * FIT_PADDING;
    void controls.fitToBox(box, animate, {
      paddingLeft: padding,
      paddingRight: padding,
      paddingTop: padding,
      paddingBottom: padding,
    });
  }

  reset(false);

  return {
    controls,
    update: (delta) => controls.update(delta),
    setPivot: (point) => controls.setOrbitPoint(point.x, point.y, point.z),
    setEnabled: (enabled) => {
      controls.enabled = enabled;
    },
    fit,
    reset,
    dispose: () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
      controls.dispose();
    },
  };
}
